import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";
import { useUserNames } from "@/lib/useUserNames";

/**
 * Printable advance / part-payment receipt.
 * Route: /print/receipt/:invoiceId/:idx
 * idx is the index of the advance in invoice.advances[].
 */
export default function PrintReceipt() {
  const { invoiceId, idx } = useParams();
  const [inv, setInv] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const letterhead = useLetterheadMode();
  const nameOf = useUserNames();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/billing/invoices/${invoiceId}`);
      setInv(data);
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      const auto = new URLSearchParams(window.location.search).get("autoprint");
      if (auto !== "0") setTimeout(() => window.print(), 600);
    })();
  }, [invoiceId]);

  if (!inv) return <div className="p-10 text-sm text-ink-muted">Loading…</div>;
  const i = parseInt(idx, 10) || 0;
  const a = (inv.advances || [])[i];
  if (!a) return <div className="p-10 text-sm text-rose-700">Receipt not found.</div>;

  // Number-to-words helper (short, INR-aware) — works for amounts up to 9,99,99,999
  const inWords = (n) => {
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
                  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (x) => x < 20 ? ones[x] : (tens[Math.floor(x / 10)] + (x % 10 ? " " + ones[x % 10] : ""));
    const three = (x) => {
      const h = Math.floor(x / 100), rest = x % 100;
      return (h ? ones[h] + " Hundred" + (rest ? " " : "") : "") + (rest ? two(rest) : "");
    };
    const rupees = Math.floor(n);
    if (rupees === 0) return "Zero";
    const crore = Math.floor(rupees / 10000000);
    const lakh  = Math.floor((rupees % 10000000) / 100000);
    const thou  = Math.floor((rupees % 100000) / 1000);
    const rest  = rupees % 1000;
    return [
      crore && two(crore) + " Crore",
      lakh  && two(lakh)  + " Lakh",
      thou  && two(thou)  + " Thousand",
      rest  && three(rest),
    ].filter(Boolean).join(" ").trim();
  };

  const receiptNo = a.receipt_no || inv.invoice_no || "";
  const receiptType = a.receipt_type || "Advance";

  return (
    <div className="receipt-a5 mx-auto bg-white p-6 text-sm text-ink" data-testid="print-receipt">
      <style>{`
        @page { size: A5 landscape; margin: 8mm; }
        .receipt-a5 { width: 210mm; max-width: 210mm; min-height: 148mm; }
        @media print {
          html, body { width: 210mm; height: 148mm; margin: 0 !important; padding: 0 !important; background: white; }
          .receipt-a5 { width: 210mm !important; height: 148mm !important; box-shadow: none !important; }
        }
      `}</style>
      <LetterheadToggle />
      {!letterhead && (
        <PrintHeader
          compact
          hospital={hospital}
          branch={branch}
          subtitle={`${receiptType} Receipt`}
          right={<>
            <div>Receipt: <span className="font-mono font-semibold">{receiptNo}</span></div>
            <div>Date: {(a.received_at || "").slice(0, 16).replace("T", " ")}</div>
            {a.cancelled && <div className="mt-1 inline-block rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700">CANCELLED</div>}
          </>}
        />
      )}

      <div className="mt-3 rounded border border-border bg-secondary/30 p-3">
        <div className="grid grid-cols-3 gap-3 text-[11px]">
          <div><div className="text-[9px] uppercase text-ink-muted">Received from</div><div className="text-sm font-semibold">{inv.patient_name}</div></div>
          <div><div className="text-[9px] uppercase text-ink-muted">UHID</div><div className="font-mono">{inv.patient_uhid}</div></div>
          <div><div className="text-[9px] uppercase text-ink-muted">Against invoice</div><div className="font-mono">{inv.invoice_no}</div></div>
          <div><div className="text-[9px] uppercase text-ink-muted">Payment mode</div><div className="capitalize">{(a.mode || "").replace("_", " ")}</div></div>
          {a.reference && <div><div className="text-[9px] uppercase text-ink-muted">Reference</div><div className="font-mono">{a.reference}</div></div>}
          {a.received_by && <div><div className="text-[9px] uppercase text-ink-muted">Received by</div><div>{nameOf(a.received_by, a.received_by)}</div></div>}
        </div>
      </div>

      {/* Amount card */}
      <div className="mt-3 rounded border-2 border-brand p-3 text-center">
        <div className="text-[10px] uppercase tracking-wider text-ink-muted">Amount received</div>
        <div className="mt-1 font-heading text-3xl font-bold text-brand">₹ {Number(a.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
        <div className="mt-1 text-[11px] italic text-ink-muted">Rupees {inWords(a.amount)} Only</div>
      </div>

      {a.notes && <div className="mt-2 text-[11px] text-ink-muted"><b>Notes:</b> {a.notes}</div>}
      {a.cancelled && a.cancel_reason && (
        <div className="mt-2 rounded border border-rose-300 bg-rose-50 p-2 text-[11px] text-rose-900">
          <b>Cancellation reason:</b> {a.cancel_reason}
          {a.cancelled_at && <span className="ml-2 text-[10px]">({a.cancelled_at.slice(0, 16).replace("T", " ")} · {nameOf(a.cancelled_by, a.cancelled_by)})</span>}
        </div>
      )}

      <div className="mt-6 flex justify-between text-[10px] text-ink-muted">
        <div>Patient / Attendant signature</div>
        <div>For {hospital?.name || BRAND.name} · Cashier</div>
      </div>

      <div className="mt-4 border-t border-border pt-2 text-center text-[10px] text-ink-muted">{BRAND.footer}</div>
    </div>
  );
}
